/**
 * StudyLogo — single component that renders a study-specific glyph.
 *
 * ┌─ STATUS ──────────────────────────────────────────────────────┐
 * │  These SVGs are honest hand-crafted placeholders, not          │
 * │  brand-tier art. They give the catalog visual identity while   │
 * │  we wait for designer- or AI-generated logos. Each is ~500B,   │
 * │  uses `currentColor`, and follows a shared visual grammar      │
 * │  (64×64 viewBox, 2.5px stroke, rounded line caps).             │
 * └────────────────────────────────────────────────────────────────┘
 *
 * UPGRADING TO BETTER ART LATER
 * Every logo is one self-contained `<svg>` in the LOGO_MAP below.
 * To swap one for designer / Midjourney output:
 *   1. Drop the image into src/assets/studies/<key>.svg|png
 *   2. Replace that entry in LOGO_MAP with
 *        () => <img src={imported} className="w-full h-full" alt="" />
 *   3. Done — every tile that reads the key picks it up.
 *
 * No code change needed on the consumer side; ModuleTile / past-
 * studies cards / Jobs page just keep passing the studyKey.
 *
 * FALLBACK
 * Keys without a registry entry render the Lucide icon hinted by
 * `template.icon` (the same icon the catalog uses today). That
 * keeps the existing 30+ template catalog working without us
 * having to draw every glyph up-front.
 */
import { Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";


export interface StudyLogoProps {
  /** Canonical study key, e.g. "demand-forecasting".
   *  Matches study.study_type / template.key from the BE. */
  studyKey?: string | null;
  /** What to render when the studyKey isn't in the registry —
   *  typically a Lucide icon element the caller already has in
   *  scope (lets the bundler keep tree-shaking lucide-react).
   *  Falls back to <Sparkles/> if not provided. */
  fallback?: React.ReactNode;
  /** Size in pixels; the SVG scales to a square of this width. */
  size?: number;
  className?: string;
}


// ──────────────────────────────────────────────────────────────
// Logo registry. Each entry is a function returning an <svg> so
// the component bundle stays tree-shake-friendly (only the keys
// React actually renders end up in the parsed output).
// ──────────────────────────────────────────────────────────────

type LogoFn = () => JSX.Element;

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};
const accentFill = { fill: "currentColor", fillOpacity: 0.12 };

const LOGO_MAP: Record<string, LogoFn> = {
  // ── Sales / Forecasting ────────────────────────────────────
  "demand-forecasting": () => (
    <svg viewBox="0 0 64 64" {...stroke}>
      {/* y-axis */}
      <line x1="10" y1="10" x2="10" y2="54" />
      {/* x-axis */}
      <line x1="10" y1="54" x2="56" y2="54" />
      {/* Historical (solid) — gentle climb */}
      <path d="M 12 44 L 20 38 L 26 40 L 32 28" />
      {/* Forecast divider */}
      <line x1="32" y1="14" x2="32" y2="54" strokeDasharray="2 3" opacity="0.4" />
      {/* Forecast (dashed) — projects upward */}
      <path d="M 32 28 L 40 22 L 48 18 L 54 14" strokeDasharray="3 3" />
      {/* Highlight dot at the peak */}
      <circle cx="54" cy="14" r="2.2" {...accentFill} />
    </svg>
  ),

  "target-sales-planning": () => (
    <svg viewBox="0 0 64 64" {...stroke}>
      {/* Bullseye */}
      <circle cx="30" cy="34" r="20" {...accentFill} />
      <circle cx="30" cy="34" r="13" />
      <circle cx="30" cy="34" r="6" />
      <circle cx="30" cy="34" r="1.6" {...accentFill} stroke="none" />
      {/* Arrow piercing the target */}
      <line x1="42" y1="22" x2="54" y2="10" />
      <path d="M 50 10 L 54 10 L 54 14" />
      {/* Fletching */}
      <path d="M 38 26 L 34 22 M 38 26 L 42 22" />
    </svg>
  ),

  // ── Operations / Inventory ─────────────────────────────────
  "inventory-optimization": () => (
    <svg viewBox="0 0 64 64" {...stroke}>
      {/* Stack of three boxes — bottom widest, top has a check */}
      <rect x="14" y="40" width="36" height="14" rx="1.5" {...accentFill} />
      <rect x="18" y="26" width="28" height="14" rx="1.5" />
      <rect x="22" y="12" width="20" height="14" rx="1.5" {...accentFill} />
      {/* Check mark on the top box */}
      <path d="M 26 19 L 30 23 L 38 15" strokeWidth="2" />
    </svg>
  ),

  "replenishment-planning": () => (
    <svg viewBox="0 0 64 64" {...stroke}>
      {/* Circular refill arrows around a box */}
      <path d="M 18 24 A 18 18 0 1 1 14 38" />
      <path d="M 18 24 L 22 22 M 18 24 L 16 28" />
      <path d="M 46 40 A 18 18 0 1 1 50 26" />
      <path d="M 46 40 L 42 42 M 46 40 L 48 36" />
      {/* Box in the centre */}
      <rect x="24" y="24" width="16" height="16" rx="1.5" {...accentFill} />
      <line x1="24" y1="30" x2="40" y2="30" opacity="0.55" />
    </svg>
  ),

  "assortment-planning": () => (
    <svg viewBox="0 0 64 64" {...stroke}>
      {/* 2 × 3 grid of variant tiles */}
      <rect x="10" y="12" width="14" height="14" rx="1.5" />
      <rect x="26" y="12" width="14" height="14" rx="1.5" {...accentFill} />
      <rect x="42" y="12" width="14" height="14" rx="1.5" />
      <rect x="10" y="28" width="14" height="14" rx="1.5" {...accentFill} />
      <rect x="26" y="28" width="14" height="14" rx="1.5" />
      <rect x="42" y="28" width="14" height="14" rx="1.5" {...accentFill} />
      {/* Highlight indicator — assortment "pick" */}
      <circle cx="48" cy="48" r="6" {...accentFill} />
      <path d="M 45 48 L 48 51 L 52 46" strokeWidth="2" />
    </svg>
  ),

  // ── Production ─────────────────────────────────────────────
  "production-planning": () => (
    <svg viewBox="0 0 64 64" {...stroke}>
      {/* Sawtooth roof + factory body */}
      <path d="M 10 24 L 18 16 L 26 24 L 34 16 L 42 24" />
      <path d="M 10 24 L 10 50 L 50 50 L 50 24" />
      {/* Chimney */}
      <path d="M 46 14 L 46 4 L 54 4 L 54 14" />
      {/* Window grid */}
      <rect x="16" y="30" width="6" height="6" {...accentFill} />
      <rect x="26" y="30" width="6" height="6" {...accentFill} />
      <rect x="36" y="30" width="6" height="6" {...accentFill} />
      {/* Door */}
      <path d="M 26 50 L 26 42 L 34 42 L 34 50" />
    </svg>
  ),

  "raw-material-planning": () => (
    <svg viewBox="0 0 64 64" {...stroke}>
      {/* Stacked rough cubes with 3-D iso facets */}
      <path d="M 14 36 L 24 30 L 34 36 L 24 42 Z" {...accentFill} />
      <path d="M 24 42 L 24 50 L 14 44 L 14 36" />
      <path d="M 24 42 L 24 50 L 34 44 L 34 36" />
      <path d="M 30 22 L 40 16 L 50 22 L 40 28 Z" />
      <path d="M 40 28 L 40 36 L 30 30 L 30 22" />
      <path d="M 40 28 L 40 36 L 50 30 L 50 22" {...accentFill} />
    </svg>
  ),

  "procurement-planning": () => (
    <svg viewBox="0 0 64 64" {...stroke}>
      {/* Cart body */}
      <path d="M 8 14 L 14 14 L 20 40 L 50 40" />
      <path d="M 16 22 L 54 22 L 50 36 L 18 36" {...accentFill} />
      {/* Wheels */}
      <circle cx="24" cy="50" r="3.5" {...accentFill} />
      <circle cx="46" cy="50" r="3.5" {...accentFill} />
      {/* Loop tags */}
      <line x1="22" y1="22" x2="22" y2="36" opacity="0.45" />
      <line x1="36" y1="22" x2="36" y2="36" opacity="0.45" />
    </svg>
  ),

  // ── Logistics ──────────────────────────────────────────────
  "first-mid-mile-optimization": () => (
    <svg viewBox="0 0 64 64" {...stroke}>
      {/* Winding route with waypoints */}
      <path d="M 8 50 Q 18 50 18 38 Q 18 26 30 26 Q 42 26 42 14" {...stroke} fill="none" strokeDasharray="3 3" />
      {/* Origin pin */}
      <circle cx="8" cy="50" r="3" {...accentFill} />
      {/* Mid-waypoint */}
      <circle cx="30" cy="26" r="2.5" />
      {/* Truck at the destination */}
      <rect x="36" y="6" width="14" height="10" rx="1.5" {...accentFill} />
      <path d="M 50 10 L 56 10 L 58 14 L 58 16 L 50 16" />
      <circle cx="42" cy="18" r="2.5" />
      <circle cx="54" cy="18" r="2.5" />
    </svg>
  ),

  // ── Finance ────────────────────────────────────────────────
  "opex-planning": () => (
    <svg viewBox="0 0 64 64" {...stroke}>
      {/* Calendar */}
      <rect x="10" y="14" width="44" height="40" rx="3" {...accentFill} />
      <line x1="10" y1="24" x2="54" y2="24" />
      {/* Hangers */}
      <line x1="20" y1="10" x2="20" y2="18" />
      <line x1="44" y1="10" x2="44" y2="18" />
      {/* $ symbol in the body */}
      <path d="M 36 32 Q 28 32 28 38 Q 28 42 32 42 Q 36 42 36 46 Q 36 50 28 50" />
      <line x1="32" y1="28" x2="32" y2="52" />
    </svg>
  ),

  "capex-planning": () => (
    <svg viewBox="0 0 64 64" {...stroke}>
      {/* Tall building */}
      <path d="M 16 54 L 16 22 L 36 14 L 36 54 Z" {...accentFill} />
      <line x1="36" y1="22" x2="48" y2="22" />
      <path d="M 48 22 L 48 54 L 36 54" />
      {/* Window grid */}
      <rect x="20" y="26" width="4" height="5" opacity="0.55" />
      <rect x="28" y="26" width="4" height="5" opacity="0.55" />
      <rect x="20" y="34" width="4" height="5" opacity="0.55" />
      <rect x="28" y="34" width="4" height="5" opacity="0.55" />
      <rect x="20" y="42" width="4" height="5" opacity="0.55" />
      <rect x="28" y="42" width="4" height="5" opacity="0.55" />
      {/* Investment arrow */}
      <path d="M 50 14 L 56 8 M 56 8 L 52 8 M 56 8 L 56 12" />
    </svg>
  ),

  "financial-planning": () => (
    <svg viewBox="0 0 64 64" {...stroke}>
      {/* Spreadsheet outline */}
      <rect x="8" y="12" width="48" height="40" rx="2" {...accentFill} />
      <line x1="8" y1="22" x2="56" y2="22" />
      <line x1="20" y1="22" x2="20" y2="52" opacity="0.45" />
      <line x1="32" y1="22" x2="32" y2="52" opacity="0.45" />
      <line x1="44" y1="22" x2="44" y2="52" opacity="0.45" />
      {/* Mini ascending bar chart on top of the rows */}
      <rect x="13" y="40" width="4" height="8" {...accentFill} />
      <rect x="25" y="34" width="4" height="14" {...accentFill} />
      <rect x="37" y="28" width="4" height="20" {...accentFill} />
      <rect x="49" y="32" width="4" height="16" {...accentFill} />
    </svg>
  ),

  // ── Strategic ──────────────────────────────────────────────
  "project-prioritization": () => (
    <svg viewBox="0 0 64 64" {...stroke}>
      {/* Three podium bars, 1st in centre */}
      <rect x="10" y="38" width="14" height="20" rx="1.5" {...accentFill} />
      <rect x="26" y="24" width="14" height="34" rx="1.5" />
      <rect x="42" y="44" width="14" height="14" rx="1.5" {...accentFill} />
      {/* Crown / star above the winner */}
      <path d="M 28 18 L 33 12 L 38 18 L 36 20 L 30 20 Z" {...accentFill} />
    </svg>
  ),
};


// Aliases — same logo for closely-related study keys so we don't
// duplicate SVGs while still rendering everything that exists.
const ALIASES: Record<string, string> = {
  "production-scheduling":          "production-planning",
  "detailed-production-scheduling": "production-planning",
  "budget-management":              "financial-planning",
  "cost-analysis":                  "financial-planning",
  "revenue-planning":               "target-sales-planning",
  "investment-analysis":            "capex-planning",
  "cash-flow":                      "financial-planning",
  "dynamic-pricing":                "target-sales-planning",
  "price-optimization":             "target-sales-planning",
};


export function StudyLogo({
  studyKey,
  fallback,
  size = 32,
  className,
}: StudyLogoProps) {
  const resolvedKey = studyKey && ALIASES[studyKey] ? ALIASES[studyKey] : studyKey;
  const render = resolvedKey ? LOGO_MAP[resolvedKey] : undefined;

  if (render) {
    return (
      <span
        aria-hidden="true"
        className={cn("inline-flex items-center justify-center", className)}
        style={{ width: size, height: size }}
      >
        {render()}
      </span>
    );
  }

  // Fallback: render whatever the caller passed in (lets the
  // bundler tree-shake lucide-react instead of pulling in every
  // icon for a string→component lookup table). Defaults to a
  // sparkles glyph so we never render nothing for unknown keys.
  return (
    <span
      aria-hidden="true"
      className={cn("inline-flex items-center justify-center", className)}
      style={{ width: size, height: size }}
    >
      {fallback ?? <Sparkles width={size * 0.75} height={size * 0.75} />}
    </span>
  );
}
